<template>
    <v-dialog v-model="show" max-width="430" persistent no-click-animation>
        <div class="UploadDataFileDialog">
            <!--Header dialog-->
            <div class="dialog-header">
                <div>{{ uploadResult ? $f.tc('resultUploadFile') : $f.tc('uploadFileData') }}</div>
                <v-btn small icon @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>
            <div class="dialog-content normalization-notification" v-if="campaign.normalization_script_description">
                This campaign use normalization script.
                <v-tooltip right>
                    <template v-slot:activator="{ on }">
                        <span v-on="on" class="detail-text" style="text-decoration: underline; cursor: pointer;">
                            Detail
                        </span>
                    </template>
                    <span>{{ campaign.normalization_script_description }}</span>
                </v-tooltip>
            </div>
            <div class="dialog-content normalization-notification"
                 v-if="customerProperties && Object.keys(customerProperties).length>0">
                This campaign applies conditions to customer properties.
                <v-tooltip right>
                    <template v-slot:activator="{ on }">
                        <span v-on="on" class="detail-text" style="text-decoration: underline; cursor: pointer;">
                            Detail
                        </span>
                    </template>
                    <div>Customer properties need to meet following conditions:</div>
                    <li v-for="(valid,index) in convertValidateProperties(customerProperties)"
                        :key="index">
                        {{ valid.key ? valid.key : '' }}
                        {{ valid.operator ? valid.operator : '' }}
                        {{ valid.threshold ? valid.threshold : '' }}
                    </li>
                </v-tooltip>
            </div>

            <!--Content dialog-->
            <v-form v-model="formValid" ref="form" @submit.prevent="submit" v-show="!uploadResult"
                    class="dialog-content">
                <!--Name field mapping with "Name"-->
                <div class="label">{{ $f.tc('nameFieldMapping') }}</div>
                <v-text-field
                    class="input-custom"
                    v-model="name"
                    :rules="formRules.required"
                ></v-text-field>

                <!--Name field mapping with "Phone"-->
                <div class="label">{{ $f.tc('phoneFieldMapping') }}</div>
                <v-text-field
                    class="input-custom"
                    v-model="phone"
                    :rules="formRules.required"
                ></v-text-field>

                <!--File upload-->
                <div class="label">{{ $f.tc('fileUpload') }}</div>
                <v-text-field
                    class="input-custom"
                    :placeholder="$f.tc('chooseFile')"
                    v-model="fileName"
                    :rules="formRules.required"
                    @click="pickFile"
                    readonly
                ></v-text-field>
                <input type="file" ref="file" v-show="false" @change="onFilePicked">

                <!--Check for previous identical call list-->
                <v-checkbox
                    v-model="checkDuplicateCallList"
                    :label="$f.tc('checkDuplicateCallList')"
                    color="#0052CC"
                    :hide-details="true"
                ></v-checkbox>
            </v-form>

            <!--Dialog footer-->
            <div class="dialog-footer" v-show="!uploadResult">
                <div class="btn-custom" @click="downloadTemplate" style="margin-right: auto">
                    <img alt="icon-download-template" src="/img/download-white.svg"
                         style="transform: scale(1.05); margin-right: 8px">
                    Download template
                </div>
                <div class="btn-custom mr-8" @click="submit">{{ $f.tc('upload') }}</div>
                <div class="btn-outline" @click="close">{{ $f.tc('cancel') }}</div>
            </div>

            <!--Content when updating/handle file-->
            <template v-if="uploadResult">
                <div class="text-upload fci-scrollbar">
                    <div v-if="uploadResult.status === CONST.CustomerFileUpLoadStatus.PROCESSING" class="centerY"
                         style="font-family: 'open sans',serif">
                        <v-progress-circular
                            color="#0052CC"
                            indeterminate
                            class="mr-12"
                            size="70"
                            style="width: 80px;"
                        ></v-progress-circular>
                        <div>
                            {{ $f.tc('textKeepOpen') }}
                            <div style="font-style: italic; font-family: 'open sans semi',serif; font-size: 13px">*Last
                                check:
                                {{ lastCheckStatus ? lastCheckStatus : $f.tc('unknown') }}
                            </div>
                        </div>
                    </div>
                    <div v-if="uploadResult.status === CONST.CustomerFileUpLoadStatus.ERROR" class="centerY"
                         style="font-family: 'open sans',serif; font-size: 14px; color: #e53d3f">
                        <img src="/img/notification/error.svg" alt="icon-error" style="width: 22px" class="mr-6">
                        Error occurred, can not process file!
                    </div>
                    <ul v-if="uploadResult.status === CONST.CustomerFileUpLoadStatus.ERROR && uploadResult.fileErrors.length > 0"
                        class="mt-6" style="font-family: 'open sans',serif; font-size: 14px">
                        <li v-for="(error, idx) in uploadResult.fileErrors" :key="`file_error_${idx}`">{{ error }}</li>
                    </ul>
                    <div v-if="uploadResult.status === CONST.CustomerFileUpLoadStatus.COMPLETED">
                        <div class="d-flex">
                            <div class="total item-card ml-0 mb-0 mr-6 text-center">
                                <div class="title mb-12 mt-10">{{ $f.tc('totalRecord') }}</div>
                                <div class="result-number" style="font-size: 55px; color: #0052cc">
                                    {{ uploadResult.record }}
                                </div>
                            </div>
                            <div>
                                <div class="display-flex item-card justify-space-between align-center">
                                    <img src="/img/notification/success.svg" alt="icon-success" style="width: 24px"
                                         class="mr-6">
                                    <div class="title">{{ $f.tc('created') }}</div>
                                    <v-spacer></v-spacer>
                                    <div class="result-number" style="color: #02543e">{{ uploadResult.created }}</div>
                                </div>
                                <div class="display-flex item-card justify-space-between align-center">
                                    <img src="/img/notification/update.svg" alt="icon-update" style="width: 24px"
                                         class="mr-6">
                                    <div class="title">{{ $f.tc('updated') }}</div>
                                    <v-spacer></v-spacer>
                                    <div class="result-number" style="color: #6763fe">{{ uploadResult.updated }}</div>
                                </div>
                                <div class="display-flex item-card justify-space-between align-center mb-0">
                                    <img src="/img/notification/error.svg" alt="icon-error" style="width: 24px"
                                         class="mr-6">
                                    <div class="title">{{ $f.tc('error') }}</div>
                                    <v-spacer></v-spacer>
                                    <div class="result-number" style="color: #e53d3f">{{ uploadResult.error }}</div>
                                </div>
                            </div>
                        </div>
                        <div v-if="uploadResult.error!==0">
                            <div class="centerY mt-12" style="cursor: pointer"
                                 @click="isShowDetailError = !isShowDetailError">
                                <div style="font-size: 14px; font-family: 'open sans bold',serif" class="mr-1">
                                    {{ $f.tc('detailedError') }}
                                </div>
                                <img src="/img/arrow-down.svg" alt="icon-collapse"
                                     :style="{transform: `${isShowDetailError ? 'rotate(180deg)' : 'rotate(0deg)'}`}"
                                     style="width: 18px; transition: 0.3s">
                            </div>
                            <v-expand-transition>
                                <div v-show="isShowDetailError">
                                    <table class="errors-table mt-6" v-if="uploadResult.errorDetail.length > 0">
                                        <thead>
                                        <tr>
                                            <th class="text-center">{{ $f.tc('rowNumber') }}</th>
                                            <th v-if="uploadResult.hasPhoneColumn">Phone number</th>
                                            <th>{{ $f.tc('error') }}</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr v-for="(detailItem, index) in uploadResult.errorDetail"
                                            :key="`error_${index}`">
                                            <td class="text-top-left">
                                                <template v-if="detailItem.rows.length === 0">—</template>
                                                <template v-else-if="uploadResult.hasPhoneColumn">
                                                    <div v-for="(row, idx) in detailItem.rows"
                                                         :key="`row_${row.num}_${idx}`">{{ row.num }}
                                                    </div>
                                                </template>
                                                <template v-else>
                                                    {{ detailItem.rows.map(row => row.num).join(', ') }}
                                                </template>
                                            </td>
                                            <td class="text-top-left" v-if="uploadResult.hasPhoneColumn">
                                                <template v-if="detailItem.rows.length === 0">—</template>
                                                <div v-for="(row, idx) in detailItem.rows"
                                                     :key="`phone_${row.num}_${idx}`">{{ row.phone || '—' }}
                                                </div>
                                            </td>
                                            <td class="text-top-left" style="min-width: 120px">
                                                <ol v-if="detailItem.errors.length > 1">
                                                    <li v-for="(error, idx) in detailItem.errors"
                                                        :key="`${error}_${idx}`">
                                                        {{ error }}
                                                    </li>
                                                </ol>
                                                <div v-if="detailItem.errors.length === 1">
                                                    {{ detailItem.errors.toString() }}
                                                </div>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                    <div class="errors-note mt-6" v-if="uploadResult.notShownErrorCount > 0">
                                        {{ uploadResult.notShownErrorCount }} more error(s) are not listed here.
                                        Send the result by email to see the full error list.
                                    </div>
                                </div>
                            </v-expand-transition>
                        </div>
                    </div>
                </div>
                <div class="dialog-footer" v-if="uploadResult.status !== CONST.CustomerFileUpLoadStatus.PROCESSING">
                    <div class="btn-custom" @click="sendEmailUploadResult" style="margin-right: 5px"
                         v-if="uploadResult.status === CONST.CustomerFileUpLoadStatus.COMPLETED">SEND EMAIL
                    </div>
                    <div class="btn-custom" @click="close">OK</div>
                </div>
            </template>
            <v-layout align-center justify-center class="uploading"
                      v-if="0 < uploadPercentage && uploadPercentage < 100">
                <v-progress-circular
                    :rotate="-90"
                    :size="50"
                    :width="3"
                    :value="uploadPercentage"
                    :style="'font-size: 10px; font-weight: bold'">
                    {{ uploadPercentage }}
                </v-progress-circular>
            </v-layout>
        </div>
    </v-dialog>
</template>

<script>
import FormMixin from "@/modules/fptai/mixins/FormMixin";
import DialogMixin from "@/modules/fptai/mixins/DialogMixin";
import FormFileMixin from "@/modules/fptai/mixins/FormFileMixin";
import CampaignService from "@/services/campaign.service";
import lodash from "lodash";
import {mapState} from "vuex";

const nameDefault = 'Name'
const phoneDefault = 'Phone'

export default {
    name: "UploadDataFileDialog",
    mixins: [FormMixin, DialogMixin, FormFileMixin],
    props: {
        campaign: Object
    },
    data() {
        return {
            name: nameDefault,
            phone: phoneDefault,
            unique: phoneDefault,
            checkDuplicateCallList: false,
            uploadPercentage: 0,
            uploadResult: null,
            promise: null,
            lastCheckStatus: null,
            keepCheckingStatus: false,
            CONST: this.$const,
            isShowDetailError: true,
            fileUploadId: null,
        }
    },
    created() {
        let name = this.$cookies.get('fieldNameHeader')
        this.name = name ? name : nameDefault
        let phone = this.$cookies.get('fieldPhoneHeader')
        this.phone = phone ? phone : phoneDefault
    },
    computed: {
        ...mapState({
            customerProperties:  state => state.campaign.campaignConfig ? state.campaign.campaignConfig.validate_customer_properties : {}
        }),
    },
    methods: {
        checkUploadStatus() {
            if (!this.keepCheckingStatus) {
                return
            }
            if (this.campaign.id) {
                setTimeout(() => {
                    CampaignService.checkUploadCustomerDataFile(this.campaign.id).then(
                        result => {
                            let errorDetail = result.data['error_detail'] ? result.data['error_detail'] : "[]"
                            let listErrors = JSON.parse(errorDetail)
                            result.data.errorDetail = this.convertDetailError(listErrors)
                            result.data.errorDetail.forEach(i => {
                                i.rows.sort(function (a, b) {
                                    return a.num - b.num;
                                })
                            })
                            // Script imports return the phone number of each failed customer, the row
                            // number alone can point to the wrong Excel row when a script groups rows
                            result.data.hasPhoneColumn = listErrors.some(i => i.phone_number)
                            // Backend hides validation errors of script imports and keeps at most 1000
                            // entries, so the error count can be larger than the listed entries
                            result.data.notShownErrorCount = Math.max((result.data.error || 0) - listErrors.length, 0)
                            // Entries without index describe the whole file, not a row
                            result.data.fileErrors = lodash.flatMap(
                                result.data.errorDetail.filter(i => i.rows.length === 0), i => i.errors
                            )

                            this.uploadResult = result.data
                            switch (result.data.status) {
                                case this.$const.CustomerFileUpLoadStatus.COMPLETED:
                                    this.$appService.toastSuccess('Importing data completed')
                                    this.promise.resolve()
                                    break
                                case this.$const.CustomerFileUpLoadStatus.ERROR:
                                    // Keep the dialog open so the user can read why the file failed
                                    this.$appService.toastError('Importing data error')
                                    this.promise.reject()
                                    break
                                default:
                                    this.checkUploadStatus()
                            }
                        },
                        this.$appService.handleRequestError('can not check upload status')
                    ).finally(() => this.lastCheckStatus = new Date().toLocaleString())
                }, 2000)
            } else {
                this.$appService.toastError('Cannot get id campaign!')
            }
        },
        convertDetailError(listErrors = []) {
            let errors = []
            listErrors.forEach(i => {
                let original_errs = Object.keys(i.errors).map((key) => i.errors[key].reduce((crVal, nextVal) => crVal === '' ? crVal + nextVal : (crVal + '; ' + nextVal), ''))
                let formatted_errs = [...new Set(original_errs)]
                // index is null when the whole file failed, null + 2 would point at row 2
                let rows = i.index === null || i.index === undefined
                    ? []
                    : [{num: i.index + 2, phone: i.phone_number || ''}]
                let checkExistError = errors.find(i => lodash.isEqual(i.errors, formatted_errs))
                if (checkExistError) {
                    checkExistError.rows.push(...rows)
                } else {
                    errors.push({
                        rows: rows,
                        errors: formatted_errs
                    })
                }
            })
            return errors
        },
        submitValidForm() {
            let formData = new FormData()
            const checkCallList = this.checkDuplicateCallList ? '1' : '0'
            formData.append('file', this.file)
            formData.append('name', this.name)
            formData.append('phone', this.phone)
            formData.append('check_duplicate_call_list', checkCallList)
            this.$cookies.set('fieldNameHeader', this.name)
            this.$cookies.set('fieldPhoneHeader', this.phone)
            this.$appService.showLoading()
            this.$http.post(
                `/campaigns/${this.campaign.id}/import-customers/`,
                formData,
                {
                    headers: {'Content-Type': 'multipart/form-data'},
                    onUploadProgress: (progressEvent) => {
                        this.uploadPercentage = Math.round(
                            Math.floor((progressEvent.loaded * 100) / progressEvent.total)
                        )
                    }
                }
            ).then(
                result => {
                    if (!result?.data?.file_upload_id) {
                        this.close()
                        this.$appService.toastError(this.$t('uploadCustomersFail'))
                        return
                    }

                    this.uploadResult = {
                        status: this.$const.CustomerFileUpLoadStatus.PROCESSING
                    }
                    this.keepCheckingStatus = true
                    this.fileUploadId = result['data']['file_upload_id']
                    this.checkUploadStatus()
                }
            ).finally(this.$appService.hideLoading)
        },
        reset() {
            this.$refs.form.reset()
            setTimeout(() => {
                let name = this.$cookies.get('fieldNameHeader')
                this.name = name ? name : nameDefault
                let phone = this.$cookies.get('fieldPhoneHeader')
                this.phone = phone ? phone : phoneDefault
                this.unique = phone ? phone : phoneDefault
                this.fileName = ''
                this.$refs.file.type = 'text'
                this.$refs.file.type = 'file'
                this.uploadPercentage = 0
                this.uploadResult = null
                this.keepCheckingStatus = false
                this.fileUploadId = null
            }, 0.001)
        },
        close() {
            if (this.uploadResult && this.uploadResult.status === this.$const.CustomerFileUpLoadStatus.COMPLETED) {
                this.promise.resolve()
            }
            this.reset()
            this.show = false
            this.$emit('close')
        },
        open() {
            this.show = true
            return new Promise((resolve, reject) => {
                this.promise = {resolve, reject}
            })
        },
        downloadTemplate() {
            CampaignService.getCustomerTemplate(this.campaign).then(res => {
                this.$utils.saveFile(res.data, this.$filters.replaceSpecialCharacters(this.$utils.nonAccentVietnamese(this.campaign.title + ' - customers data template')), '.xlsx')
            })
        },
        sendEmailUploadResult() {
            let data = {
                'file_upload_id': this.fileUploadId
            }
            CampaignService.sendEmailUploadResult(this.campaign.id, data).then(res => {
                this.$appService.toastSuccess(res.data.message)
                this.close()
            })
        },
        convertValidateProperties(data) {
            if (!data) {
                return []
            }
            const valuesArray = [];
            for (const key in data) {
                if (data.hasOwnProperty(key)) {
                    data[key].forEach(item => {
                        item["key"] = key
                        valuesArray.push(item);
                    });
                }
            }
            return valuesArray;
        }
    },
}
</script>

<style scoped lang="scss">
.UploadDataFileDialog {
    position: relative;
    background: #FFFFFF;
    border-radius: 5px !important;
    overflow: hidden;

    .label {
        margin-top: 12px;
        font-family: "open sans semi", serif;
        font-size: 13px;

        &:first-child {
            margin-top: 0 !important;
        }
    }

    .text-upload {
        font-size: 14px;
        padding: 12px;
        max-height: 60vh;
        overflow-y: auto;
    }
}

.item-card {
    font-family: "open sans semi", serif;
    background: #E4EDFA;
    margin-bottom: 6px;
    padding: 10px;
    border-radius: 5px;

    .title {
        font-size: 13px !important;
        text-transform: uppercase;
        color: #96ADD0;
    }

    .result-number {
        font-family: "open sans bold", serif;
        font-size: 20px;
    }
}

table.errors-table {
    width: 100%;
    border-collapse: collapse;

    th, td {
        border: 1px solid #E4EDFA;
        padding: 10px;
    }
}

.errors-note {
    font-family: "open sans", serif;
    font-size: 13px;
    font-style: italic;
    color: #6B778C;
}

.input-custom {
    ::v-deep {
        .v-input__slot {
            height: 32px;
            background: #FFFFFF;
            border-radius: 5px;
            border: 1px solid #D9D9D9;
            outline: none;
            font-size: 14px;
            padding: 6px 12px;
            margin: 0;
            display: flex;
            align-items: center;
            position: relative;

            &:before, &:after {
                border: none !important;
            }
        }
    }
}

::v-deep {
    .v-text-field {
        margin-top: 0 !important;
        padding-top: 0 !important;
        position: relative;
    }

    .v-text-field__details {
        position: absolute;
        right: 12px;
        top: 8px
    }

    .v-select {
        .v-text-field__details {
            right: 40px !important;
        }
    }

    .v-input--selection-controls {
        margin-top: 14px !important;
        padding-top: 0 !important;
        margin-bottom: 3px;
    }

    label {
        font-family: "open sans", serif !important;
        font-size: 14px !important;
        color: #000 !important;
    }
}

.uploading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.75);
}

.normalization-notification {
    font-family: "open sans", serif;
    font-size: 13px;
    background: #e4edfa;
    color: #0052cc;
}

</style>
